import { useTranslation } from 'react-i18next';
import { useUserRoles } from '@anytag/auth';
import { InfluencerAvatar } from '@anytag/shared-molecules';
import { Anchor, TableCell, TableV2 } from '@anytag/shared-atoms';
import { useGenerateInfluencerProfileLink, useParamsSearch } from '@anytag/shared-hooks';
import {
  getOffset,
  formatPrice,
  formatPercent,
  formatIntNumber,
  getValuableVariables,
  convertOrderToORDER
} from '@anytag/shared-utils';
import {
  MarketplaceAffiliateReportSummary,
  MarketplaceAffiliateReportByInfluencer,
  MarketplaceAffiliateReportByInfluencerField,
  useMarketplaceAffiliateReportByInfluencerQuery
} from '@anytag/graphql';
import { ColumnDef } from '@tanstack/react-table';
import { MarketplaceAffiliateReportSearchSchemaType } from '../MarketplaceAffiliateReportFilter';
import { formatAffiliatePeriod } from './utils';

export interface MarketplaceAffiliateReportByInfluencerRowData {
  className?: string;
  influencerInfo?: MarketplaceAffiliateReportByInfluencer['influencerInfo'];
  stats?: Partial<Omit<MarketplaceAffiliateReportByInfluencer, 'influencerInfo'>>;
}

interface MarketplaceAffiliateReportByInfluencerTableProps {
  currency: string;
  campaignId: number;
  isReferralCampaign?: boolean;
  summaryData?: MarketplaceAffiliateReportSummary;
}

export const MarketplaceAffiliateReportByInfluencerTable = ({
  currency,
  campaignId,
  summaryData,
  isReferralCampaign
}: MarketplaceAffiliateReportByInfluencerTableProps) => {
  const { t } = useTranslation();
  const { isAdminStaff } = useUserRoles();
  const { generateProfileLink } = useGenerateInfluencerProfileLink();
  const {
    page,
    limit,
    sort,
    filter: { startDate, endDate }
  } = useParamsSearch<MarketplaceAffiliateReportSearchSchemaType>();
  const { data, loading } = useMarketplaceAffiliateReportByInfluencerQuery({
    variables: getValuableVariables({
      limit,
      endDate,
      startDate,
      campaignId,
      offset: getOffset(page, limit),
      orderBy: { field: sort.field, order: convertOrderToORDER(sort.order) }
    })
  });

  const columns: ColumnDef<MarketplaceAffiliateReportByInfluencerRowData>[] = [
    {
      accessorKey: 'influencerName',
      meta: { sticky: true },
      header: t<string>('HeaderColumn.Influencer Name'),
      cell: ({ row }) => {
        const { influencerInfo, className } = row.original;
        const profileLink = generateProfileLink(influencerInfo?.id);

        return className === 'total-row' ? (
          <TableCell.CampaignTotal
            title={
              <TableCell.Text value={formatAffiliatePeriod({ period: { endDate, startDate }, title: t('Total') })} />
            }
          />
        ) : className === 'average-row' ? (
          <TableCell.CampaignAverage title={t('Average')} />
        ) : (
          <div className="flex items-center gap-2">
            <InfluencerAvatar size="48px" src={influencerInfo?.thumbNail} id={influencerInfo?.id} />
            <Anchor lines={3} label={influencerInfo?.name} {...profileLink} />
          </div>
        );
      }
    },
    {
      accessorKey: 'clicks',
      header: t<string>('HeaderColumn.Click'),
      meta: {
        align: 'right',
        hidden: isReferralCampaign,
        sortField: MarketplaceAffiliateReportByInfluencerField.CLICK
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.clicks)} />
    },
    {
      accessorKey: 'conversions',
      header: t<string>('HeaderColumn.Order'),
      meta: { align: 'right', sortField: MarketplaceAffiliateReportByInfluencerField.ORDER },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.conversions)} />
    },
    {
      accessorKey: 'cvr',
      header: t<string>('HeaderColumn.CVR'),
      meta: { align: 'right', hidden: isReferralCampaign, sortField: MarketplaceAffiliateReportByInfluencerField.CVR },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.stats?.cvr, false)} unit="%" />
    },
    {
      accessorKey: 'sales',
      header: t<string>('HeaderColumn.Store Sales'),
      meta: { align: 'right', sortField: MarketplaceAffiliateReportByInfluencerField.SALE },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats?.sales || null, currency)} unit={currency} />
      )
    },
    {
      accessorKey: 'cost',
      header: t<string>('HeaderColumn.Cost'),
      meta: { align: 'right', sortField: MarketplaceAffiliateReportByInfluencerField.COST },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats?.cost || null, currency)} unit={currency} />
      )
    },
    {
      accessorKey: 'commissionRate',
      header: t<string>('HeaderColumn.Influencer Commission Rate'),
      meta: {
        align: 'right',
        hidden: !isAdminStaff,
        sortField: MarketplaceAffiliateReportByInfluencerField.INFLUENCER_COMMISSION_RATE
      },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.stats?.commissionRate, false)} unit="%" />
    },
    {
      accessorKey: 'influencerCost',
      header: t<string>('HeaderColumn.Influencer Commission'),
      meta: {
        align: 'right',
        hidden: !isAdminStaff,
        sortField: MarketplaceAffiliateReportByInfluencerField.INFLUENCER_COST
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats?.influencerCost || null, currency)} unit={currency} />
      )
    },
    {
      accessorKey: 'promotionMethods',
      header: t<string>('HeaderColumn.Promotion method'),
      cell: ({ row }) => <TableCell.Text value={row.original.stats?.promotionMethods?.join(', ') || '-'} />
    }
  ];

  const tableData: MarketplaceAffiliateReportByInfluencerRowData[] = [
    { className: 'total-row', stats: summaryData?.summary },
    { className: 'average-row', stats: summaryData?.average },
    ...(data?.marketplaceAffiliateReportByInfluencer?.map<MarketplaceAffiliateReportByInfluencerRowData>(
      ({ influencerInfo, ...stats }) => ({
        stats,
        influencerInfo
      })
    ) || [])
  ];

  return (
    <TableV2
      className="report-table"
      loading={loading}
      data={tableData}
      columns={columns}
      rowHeight="73px"
      pagination={{ totalRecords: data?.marketplaceAffiliateReportByInfluencerCount?.totalNumber }}
      noTopBorderRadius
      stickyHeader
    />
  );
};
